import {Box, Flex, Label, PositionedBox, Span} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {useNavigate, useParams} from 'react-router-dom'

import {useOfferedAddons} from '../../api/addons/get-offered-addons'
import {Equipment} from '../../api/booking/types'
import {ClassQueries} from '../../api/class'
import {MemberQueries} from '../../api/member'
import {MemberParticipantType} from '../../api/member/types'
import {SubscriptionsQueries} from '../../api/subscriptions'
import {Subscription} from '../../api/subscriptions/types'
import {useVenue} from '../../api/venue/hooks'
import {addonQuantitiesElsewhere, toIsoDate, withIncludedAddonLines} from '../../helpers/addon-lines'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {useAuthState} from '../../services/auth/hooks'
import {CreateServiceBookingFormType} from '../../services/booking/forms/create-service-booking-form'
import {EditParticipantForm, EditParticipantFormProps} from '../../services/booking/forms/edit-participant-form'
import {BookingStateParticipant} from '../../services/booking/forms/types'
import {UserService} from '../../services/user'
import {Button} from '../../ui/components/button/button'
import {ExsportiaIcon} from '../../ui/components/exsportia-icon/exsportia-icon'
import {InfoMessage} from '../../ui/components/info-message/info-message'
import {ScrollableWrapper} from '../../ui/components/scrollable-wrapper/scrollable-wrapper'
import {auth} from '../../utils/firebase'
import {useLocale} from '../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../utils/logic'
import {useModalActions} from '../../utils/modals'
import {NO_USER_PHOTO} from '../../utils/remote-content'
import {EditEquipmentsModal} from './components/edit-equipments-modal'
import {EditParticipantModal} from './components/edit-participant-modal'
import {Participant} from './components/participant'
import {createBookingStateParticipant} from './helpers'

const Header: FC<{isMultiSession?: boolean; onBack?: () => void}> = ({isMultiSession, onBack}) => {
  const {l} = useLocale()
  return (
    <SimpleGridLayoutHeader
      title={isMultiSession ? l('who_is_coming') || 'Who is coming?' : l('participants')}
      customBackAction={onBack}
    />
  )
}

const bookingTypeMap = {
  service: 'SPORT',
  teacher: 'TEACHER',
  class: 'CLASS',
  space: 'SPORT',
}

// eslint-disable-next-line max-lines-per-function
export const SetParticipantsPage: FC = () => {
  const navigate = useNavigate()
  const {l} = useLocale()
  const {openModal, closeModal} = useModalActions()
  const {data: venueData} = useVenue()
  const {values, setValues, validateForm} = useFormikContext<
    CreateServiceBookingFormType & {
      classEquipments?: Equipment[]
      classId?: string
      teacherId?: string
      duration?: number | string
      sessionId?: string
      sportType?: string
      coverEquipment?: boolean
    }
  >()
  const {
    minParticipants,
    maxParticipants,
    service,
    participants: prevParticipants,
    classEquipments,
    bookingType,
    useForParticipants,
  } = values
  const {data: classData, isLoading: isClassDataLoading} = useQuery(
    ClassQueries.getClassQuery({params: {id: values.classId}}),
  )
  const classEquipmentsToUse = useMemo(
    () => (isNotNilAndNotEmpty(classEquipments) ? classEquipments : classData?.equipments),
    [classEquipments, classData?.equipments],
  )
  const {
    data: validatedSubscriptions,
    mutate,
    isLoading: isSubscriptionsLoading,
  } = useMutation(SubscriptionsQueries.validateUserSubscriptionsMutation())
  // Only `memberships` are ever offered or auto-attached; `unavailable` passes are shown, greyed out, in the chooser.
  const subscriptions = validatedSubscriptions?.memberships
  const unavailableSubscriptions = validatedSubscriptions?.unavailable
  const isLoading: boolean = useMemo(
    () => isSubscriptionsLoading || isClassDataLoading,
    [isSubscriptionsLoading, isClassDataLoading],
  )
  useEffect(() => {
    if (!venueData) {
      return
    }
    mutate({
      options: [
        {
          venueId: venueData?.uid,
          sportId: values.serviceId,
          areaId: values.area?.uid,
          teacherId: values.teacherId,
          bookingType: bookingTypeMap[values.bookingType],
          classId: values.classId,
          date: values.date,
          userId: auth.currentUser?.uid,
          consideredDate: values.date,
          sportType: classData?.sportType,
          duration: values.duration,
          isConsideredOption: true,
          slotId: values.sessionId,
        },
      ],
    })
  }, [venueData, values, classData, auth.currentUser])
  const maxParticipantsToUse = maxParticipants || service?.maxPlayers || Infinity
  const {isLoggedIn} = useAuthState()
  const {data: user} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const {data: member} = useQuery(
    MemberQueries.getMemberByVenueQuery({
      query: {
        venueId: venueData?.uid,
        userId: user?.uid,
      },
    }),
  )
  const [isSubscriptionAllocable, setIsSubscriptionAllocable] = useState(false)
  // Per-session subscription share toggle for multi-session mode
  const currentSessionIdForShare = (values as any).sessionId as string | undefined
  const initialShareForSession = currentSessionIdForShare
    ? (values as any).perSessionUseForParticipants?.[currentSessionIdForShare]
    : undefined
  const [isSubscriptionShared, setIsSubscriptionShared] = useState(
    initialShareForSession !== undefined ? initialShareForSession : useForParticipants,
  )
  // Re-sync local toggle state when sessionId changes (multi-session navigation)
  useEffect(() => {
    const sid = (values as any).sessionId as string | undefined
    if (!sid) {
      return
    }
    const stored = (values as any).perSessionUseForParticipants?.[sid]
    setIsSubscriptionShared(stored !== undefined ? stored : false)
  }, [(values as any).sessionId]) // eslint-disable-line
  const myParticipants: MemberParticipantType[] = useMemo(
    () => [
      {
        name: user?.name || `${user?.firstName} ${user?.lastName}`,
        avatar: {
          secure_url: user?.avatar || NO_USER_PHOTO,
        },
      },
      ...(member?.participants || []),
    ],
    [member?.participants, user],
  )
  const initialState = useMemo<BookingStateParticipant[]>(() => {
    const result: BookingStateParticipant[] = []
    const canSetSubscriptionAllocable =
      service?.splitPayment || service?.multiplyPerPerson || isNotNilAndNotEmpty(classData?.uid)
    if (isNotNilAndNotEmpty(values.participants)) {
      const selectedSubscription = subscriptions?.find(
        (sub) => values.participants[0].membership?.membershipBookingId === sub.membershipBookingId,
      )
      setIsSubscriptionAllocable(selectedSubscription?.useForParticipants || false)
      setIsSubscriptionShared((values.useForParticipants && canSetSubscriptionAllocable) || false)
      return values.participants
    }
    if (user) {
      if (subscriptions?.[0] && isLoggedIn) {
        setIsSubscriptionAllocable((subscriptions[0].useForParticipants && canSetSubscriptionAllocable) || false)
        setIsSubscriptionShared((subscriptions[0].useForParticipants && canSetSubscriptionAllocable) || false)
        result.push(
          createBookingStateParticipant({
            name: user.name,
            userId: user?.uid,
            avatar: user.avatar,
            memberParticipantIndex: 0,
            coverEquipment: subscriptions[0].coverEquipment,
            membership: {
              logo: subscriptions[0].logo,
              activityGroupId: subscriptions[0].activityGroupId,
              membershipBookingId: subscriptions[0].membershipBookingId,
              membershipBookingReference: subscriptions[0].membershipBookingReference,
              cancelPolicy: subscriptions[0].cancelPolicy,
              name: subscriptions[0].membershipName,
              uid: subscriptions[0].membershipId,
            },
          }),
        )
      } else {
        result.push(
          createBookingStateParticipant({
            name: user.name,
            userId: user?.uid,
            avatar: user.avatar,
            memberParticipantIndex: 0,
          }),
        )
      }
    }
    for (let i = result.length; i < minParticipants; i++) {
      result.push(
        createBookingStateParticipant({name: l('counted_participant', {value: i + 1, caseAction: 'capitalize'})}),
      )
    }
    return result
  }, [l, minParticipants, user, subscriptions, values])
  const [participants, setParticipants] = useState(initialState)
  useEffect(() => {
    setParticipants(initialState)
  }, [initialState])
  const addParticipant = useCallback(() => {
    return setParticipants((prevState) => [
      ...prevState,
      createBookingStateParticipant({
        name: l('counted_participant', {value: prevState.length + 1, caseAction: 'capitalize'}),
      }),
    ])
  }, [l, member, prevParticipants, participants])

  // set/unset user on login/logout
  useEffect(() => {
    const canSetSubscriptionAllocable =
      service?.splitPayment || service?.multiplyPerPerson || isNotNilAndNotEmpty(classData?.uid)
    const isMulti = ((values as any).selectedSessions?.length || 0) > 1
    let buildSlot0: () => BookingStateParticipant
    if (user) {
      const activeSubscription = subscriptions?.filter((el) => !el.disabledFromAssessConfig)[0]
      if (activeSubscription && isLoggedIn) {
        setIsSubscriptionAllocable((activeSubscription.useForParticipants && canSetSubscriptionAllocable) || false)
        // In multi-session mode, per-session value is owned by the sessionId effect and Next handler
        if (!isMulti) {
          setIsSubscriptionShared((activeSubscription.useForParticipants && canSetSubscriptionAllocable) || false)
        }
        buildSlot0 = () =>
          createBookingStateParticipant({
            name: user.name,
            userId: user?.uid,
            avatar: user.avatar,
            coverEquipment: activeSubscription.coverEquipment,
            memberParticipantIndex: 0,
            membership: {
              logo: activeSubscription.logo,
              cancelPolicy: activeSubscription.cancelPolicy,
              activityGroupId: activeSubscription.activityGroupId,
              membershipBookingId: activeSubscription.membershipBookingId,
              membershipBookingReference: activeSubscription.membershipBookingReference,
              name: activeSubscription.membershipName,
              uid: activeSubscription.membershipId,
            },
          })
      } else {
        buildSlot0 = () =>
          createBookingStateParticipant({
            name: user.name,
            userId: user?.uid,
            avatar: user.avatar,
            memberParticipantIndex: 0,
          })
      }
    } else {
      buildSlot0 = () =>
        createBookingStateParticipant({
          name: l('counted_participant', {value: 1, caseAction: 'capitalize'}),
        })
    }
    setParticipants((prev) => {
      const next = [...prev]
      next[0] = buildSlot0()
      return next
    })
  }, [user, subscriptions]) //eslint-disable-line

  const equipmentsToUse = useMemo(() => service?.equipments || classEquipmentsToUse, [service, classEquipmentsToUse])
  // Add-ons (Settings > Add-ons) the service or class offers, beside the old equipment. A class is asked for by its
  // CLASS id, not the session's. If the list is empty or the call fails, nothing about this step changes.
  const isClassBooking = isNotNilAndNotEmpty(values.classId)
  const {data: offered} = useOfferedAddons({
    venueId: venueData?.uid,
    entityType: isClassBooking ? 'class' : 'service',
    entityId: isClassBooking ? values.classId : values.serviceId || service?.uid,
    date: toIsoDate(values.date),
  })
  const offeredAddons = useMemo(() => offered?.addons ?? [], [offered])
  const hasOldEquipment = isNotNilAndNotEmpty(equipmentsToUse) && equipmentsToUse.length > 0
  const hasAddons = offeredAddons.length > 0
  useEffect(() => {
    // update non-user and not-edited participants
    const updatedParticipants = participants.map((participant, i) => {
      if (participant.edited || participant.avatar) {
        return participant
      }
      return {...participant, name: l('counted_participant', {value: i + 1, caseAction: 'capitalize'})}
    })
    setParticipants(updatedParticipants)
  }, [l, participants.length]) //eslint-disable-line

  const removeParticipant = useCallback(
    (index: number) => {
      const newParticipants = [...participants]
      newParticipants.splice(index, 1)
      setParticipants(newParticipants)
    },
    [participants],
  )

  const handleEditParticipant = useCallback(
    (index: number) => {
      const handleSetParticipant = ({
        name,
        subscription,
        memberParticipantIndex,
        avatar,
      }: {
        avatar: string | null
        name: string
        subscription?: Subscription
        memberParticipantIndex: number | null
      }) => {
        const participantsToSave = [...participants]
        const canSetSubscriptionAllocable =
          service?.splitPayment || service?.multiplyPerPerson || isNotNilAndNotEmpty(classData?.uid)
        if (index === 0) {
          setIsSubscriptionAllocable((subscription?.useForParticipants && canSetSubscriptionAllocable) || false)
          if (participants[index].membershipId !== subscription?.membershipId) {
            setIsSubscriptionShared(subscription?.useForParticipants && canSetSubscriptionAllocable)
          }
        }
        participantsToSave[index] = {
          ...participantsToSave[index],
          name,
          avatar: avatar || participantsToSave[index]?.avatar || NO_USER_PHOTO,
          coverEquipment: subscription?.coverEquipment,
          membershipId: subscription?.membershipId,
          // WBA-4727: `uid` on a validate/byUser row is the usageLimits doc id, NOT the
          // subscription booking. Since WBA-3809 the price endpoint filters the contact's
          // bookings by this exact id, so the usageLimit id matched nothing — the chosen
          // subscription was dropped, usage rendered as "0 entries" and the booking priced
          // at full price. membershipBookingId is the field that names the booking.
          membershipBookingId: subscription?.membershipBookingId,
          memberParticipantIndex,
          membership: subscription
            ? {
                logo: subscription.logo,
                uid: subscription.membershipId,
                name: subscription.membershipName,
                cancelPolicy: subscription.cancelPolicy,
                membershipBookingId: subscription.membershipBookingId,
                membershipBookingReference: subscription.membershipBookingReference,
              }
            : null,
        }
        setParticipants(participantsToSave)
        closeModal()
      }
      openModal({
        component: (
          <EditParticipantForm
            handleSubmit={handleSetParticipant as EditParticipantFormProps['handleSubmit']}
            initialName={participants[index].name || ''}
            initialSubscriptionBookingId={isLoggedIn ? participants[index].membership?.membershipBookingId : undefined}
            subscriptions={isLoggedIn ? subscriptions : []}
            initialMemberParticipantIndex={
              participants[index].memberParticipantIndex || participants[index].memberParticipantIndex === 0
                ? participants[index].memberParticipantIndex
                : null
            }
          >
            <EditParticipantModal //
              isLoggedIn={isLoggedIn}
              allowedSubscriptionsList={
                service?.accessConfig?.subscriptions
                  ? service.accessConfig.subscriptions
                  : classData?.accessConfig?.subscriptions
              }
              initialMemberParticipantIndex={
                participants[index].memberParticipantIndex || participants[index].memberParticipantIndex === 0
                  ? participants[index].memberParticipantIndex
                  : null
              }
              subscriptions={isLoggedIn && index === 0 ? subscriptions : undefined}
              unavailableSubscriptions={isLoggedIn && index === 0 ? unavailableSubscriptions : undefined}
              memberParticipants={myParticipants}
              anySubscriptions={
                bookingType === 'service'
                  ? service?.accessForAnyone || service?.accessConfig?.anySubscriptions
                  : !!(classData?.accessForAnyone || classData?.accessConfig?.anySubscriptions)
              }
              omittedMemberParticipants={participants.reduce((acc: number[], cur) => {
                if (
                  (cur.memberParticipantIndex || cur.memberParticipantIndex === 0) &&
                  participants[index].memberParticipantIndex !== cur.memberParticipantIndex
                ) {
                  return [...acc, cur.memberParticipantIndex]
                }
                return acc
              }, [])}
            />
          </EditParticipantForm>
        ),
        title: participants[index].name,
        footerConfig: {
          disabled: true,
        },
      })
    },
    [l, participants, openModal, closeModal, subscriptions, unavailableSubscriptions],
  )

  const handleEditParticipantEquipment = useCallback(
    (index: number) => {
      const handleSetParticipantEquipments = (equipments: Equipment[]) => {
        const participantsToSave = [...participants]
        participantsToSave[index] = {
          ...participantsToSave[index],
          equipments,
        }
        setParticipants(participantsToSave)
        closeModal()
      }
      openModal({
        title: hasOldEquipment || !hasAddons ? l('equipment') : l('addon_section_title'),
        footerConfig: {disabled: true},
        component: (
          <EditEquipmentsModal
            serviceEquipments={equipmentsToUse}
            handleSubmit={handleSetParticipantEquipments}
            currency={venueData?.currency}
            initialEquipments={participants[index].equipments}
            offeredAddons={offeredAddons}
            participantIndex={index}
            addonsBookedElsewhere={addonQuantitiesElsewhere(participants, index)}
          />
        ),
      })
    },
    [l, participants, equipmentsToUse, openModal, closeModal, venueData, offeredAddons, hasOldEquipment, hasAddons],
  )

  const participantsMarkup = useMemo(
    () =>
      participants.map((participant, index) => (
        <Participant
          index={index}
          isLoading={isLoading}
          name={participant.name}
          image={participant.avatar}
          key={`participant-${index}`}
          currency={venueData?.currency}
          equipments={participant.equipments}
          onEdit={() => handleEditParticipant(index)}
          isSubscriptionShared={isSubscriptionShared}
          selectedSubscriptionName={participant.membership?.name}
          onEquipmentEdit={() => handleEditParticipantEquipment(index)}
          handleSetIsApplySubscriptionForParticipants={setIsSubscriptionShared}
          withEquipment={hasOldEquipment || hasAddons}
          onlyAddons={!hasOldEquipment && hasAddons}
          isSubscriptionsAvailable={isLoggedIn && isNotNilAndNotEmpty(subscriptions) && index === 0}
          isSubscriptionAllocable={isSubscriptionAllocable && index === 0 && participants.length > 1}
          onRemove={index !== 0 && participants.length > minParticipants ? () => removeParticipant(index) : null}
        />
      )),
    [
      participants,
      handleEditParticipantEquipment,
      handleEditParticipant,
      subscriptions,
      isLoggedIn,
      isSubscriptionAllocable,
      isSubscriptionShared,
      isLoading,
      hasOldEquipment,
      hasAddons,
    ],
  )

  const handleNextClick = useCallback(() => {
    // Included add-ons ride along on every participant, whether or not the Add-ons pop-up was opened.
    const participantsToSave = withIncludedAddonLines(participants, offeredAddons)
    const isMultiSession = (values as any).selectedSessions?.length > 1
    const sharedConfig = (values as any).sharedParticipantsConfig !== false

    if (isMultiSession && !sharedConfig) {
      // Per-session mode: save participants for current session, advance to next
      const currentSession = (values as any).selectedSessions[(values as any).currentSessionIndex]
      const currentSessionId = currentSession?.sessionId
      const updatedPerSession = {
        ...((values as any).perSessionParticipants || {}),
        [currentSessionId]: participantsToSave,
      }
      const updatedPerSessionShare = {
        ...((values as any).perSessionUseForParticipants || {}),
        [currentSessionId]: isSubscriptionShared,
      }
      const updatedHandled = [...((values as any).handledSlots || []), currentSessionId]
      const nextIndex = (values as any).currentSessionIndex + 1
      const totalSessions = (values as any).selectedSessions.length

      if (nextIndex >= totalSessions) {
        // All sessions configured → go to summary
        setValues({
          ...values,
          participants: participantsToSave,
          perSessionParticipants: updatedPerSession,
          perSessionUseForParticipants: updatedPerSessionShare,
          handledSlots: updatedHandled,
          currentSessionIndex: nextIndex,
          useForParticipants: isSubscriptionShared,
          coverEquipment: participants[0]?.coverEquipment,
          sportType: classData?.sportType,
        } as any)
        navigate('../summary')
      } else {
        // Advance to next session
        const nextSession = (values as any).selectedSessions[nextIndex]
        const nextSessionParticipants = (values as any).perSessionParticipants?.[nextSession.sessionId] || []
        const nextSessionShare = (values as any).perSessionUseForParticipants?.[nextSession.sessionId]
        setValues({
          ...values,
          participants: nextSessionParticipants,
          perSessionParticipants: updatedPerSession,
          perSessionUseForParticipants: updatedPerSessionShare,
          handledSlots: updatedHandled,
          currentSessionIndex: nextIndex,
          sessionId: nextSession.sessionId,
          timestamp: nextSession.timestamp,
          classId: nextSession.classId,
          areaId: nextSession.areaId,
          maxParticipants: nextSession.maxParticipants,
          duration: nextSession.duration,
          date: nextSession.date,
          instructors: nextSession.instructors,
          useForParticipants: nextSessionShare !== undefined ? nextSessionShare : false,
          coverEquipment: participants[0]?.coverEquipment,
          sportType: nextSession.sportType || classData?.sportType,
        } as any)
      }
    } else {
      // Single session or shared participants mode
      if (isMultiSession && sharedConfig) {
        // Copy participants to all sessions
        const perSession: Record<string, typeof participants> = {}
        for (const s of (values as any).selectedSessions) {
          perSession[s.sessionId] = participantsToSave
        }
        setValues({
          ...values,
          participants: participantsToSave,
          perSessionParticipants: perSession,
          useForParticipants: isSubscriptionShared,
          coverEquipment: participants[0]?.coverEquipment,
          sportType: classData?.sportType,
        } as any)
      } else {
        setValues({
          ...values,
          participants: participantsToSave,
          useForParticipants: isSubscriptionShared,
          coverEquipment: participants[0]?.coverEquipment,
          sportType: classData?.sportType,
        })
      }
      const errors = validateForm()
      if (!Object.keys(errors).length) {
        navigate('../summary')
      }
    }
  }, [navigate, participants, offeredAddons, isSubscriptionShared, classData, values]) // eslint-disable-line

  const handleBack = useCallback(() => {
    const isMulti = (values as any).selectedSessions?.length > 1
    const sharedConf = (values as any).sharedParticipantsConfig !== false
    const sessions = (values as any).selectedSessions || []
    const sessionIdx = (values as any).currentSessionIndex || 0
    // Per-session multi flow: step back to the PREVIOUS session's participants step instead of
    // jumping straight out to the confirm/approve step. WBA-4046.
    if (isMulti && !sharedConf && sessionIdx > 0) {
      const prevIndex = sessionIdx - 1
      const prevSession = sessions[prevIndex]
      const currentSession = sessions[sessionIdx]
      const updatedPerSession = {
        ...((values as any).perSessionParticipants || {}),
        // preserve the current session's in-progress edits so they aren't lost on back
        ...(currentSession ? {[currentSession.sessionId]: participants} : {}),
      }
      setValues({
        ...values,
        participants: updatedPerSession[prevSession.sessionId] || [],
        perSessionParticipants: updatedPerSession,
        currentSessionIndex: prevIndex,
        sessionId: prevSession.sessionId,
        timestamp: prevSession.timestamp,
        classId: prevSession.classId,
        areaId: prevSession.areaId,
        maxParticipants: prevSession.maxParticipants,
        duration: prevSession.duration,
        date: prevSession.date,
        instructors: prevSession.instructors,
        useForParticipants: (values as any).perSessionUseForParticipants?.[prevSession.sessionId] ?? false,
        sportType: prevSession.sportType || classData?.sportType,
      } as any)
      return
    }
    // First session, or shared-participants mode → pop history to the confirm/approve step
    // (which sits directly below this one in the stack). WBA-4046.
    navigate(-1)
  }, [values, participants, setValues, navigate, classData])

  const isMultiSession = (values as any).selectedSessions?.length > 1
  const sharedConfig = (values as any).sharedParticipantsConfig !== false
  const currentSessionIdx = (values as any).currentSessionIndex || 0
  const totalSessionCount = (values as any).selectedSessions?.length || 0
  const currentSessionForDisplay = isMultiSession ? (values as any).selectedSessions?.[currentSessionIdx] : null

  return (
    <GridLayout
      header={
        <Header
          isMultiSession={isMultiSession}
          onBack={isMultiSession ? handleBack : undefined}
        />
      }
    >
      <ScrollableWrapper>
        <Box>
          {isMultiSession && !sharedConfig && currentSessionForDisplay && (
            <Box
              mb='12px'
              p='12px 16px'
              bg='#f7fafc'
              borderRadius='8px'
            >
              <Box mb='4px'>
                <Span
                  fontSize='14px'
                  fontWeight='bold'
                  color='primary'
                >
                  {`${l('session') || 'Session'} ${currentSessionIdx + 1} / ${totalSessionCount}`}
                </Span>
              </Box>
              <Span
                fontSize='12px'
                color='#718096'
              >
                {`${currentSessionForDisplay.className} · ${currentSessionForDisplay.date} · ${currentSessionForDisplay.time}`}
              </Span>
            </Box>
          )}
          {isMultiSession && sharedConfig && (
            <Flex
              mb='12px'
              flexWrap='wrap'
              gap='6px'
            >
              {(values as any).selectedSessions.map((s: any, i: number) => (
                <Box
                  key={i}
                  px='10px'
                  py='6px'
                  bg='#f7fafc'
                  borderRadius='16px'
                  border='1px solid #e2e8f0'
                >
                  <Span
                    fontSize='12px'
                    color='#4a5568'
                    fontWeight='500'
                  >
                    {`${s.date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, (_: string, d: string, m: string) => {
                      const months = [
                        'Jan',
                        'Feb',
                        'Mar',
                        'Apr',
                        'May',
                        'Jun',
                        'Jul',
                        'Aug',
                        'Sep',
                        'Oct',
                        'Nov',
                        'Dec',
                      ]
                      return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`
                    })}, ${s.time}`}
                  </Span>
                </Box>
              ))}
            </Flex>
          )}
          <Box mb='16px'>
            <Box>{participantsMarkup}</Box>
            {service?.splitPayment && (
              <Box mb='16px'>
                <InfoMessage
                  text={l('split_payment_participants_info')}
                  preset='success'
                />
              </Box>
            )}
            <Button
              disabled={participants.length >= maxParticipantsToUse}
              type='flat'
              onClick={addParticipant}
            >
              <Flex alignItems='end'>
                <ExsportiaIcon
                  icon='plus'
                  color='primary'
                />
                <Span ml='8px'>{l('addParticipant')}</Span>
              </Flex>
            </Button>
          </Box>
          <Flex
            justifyContent='center'
            mb='16px'
          >
            <Label>
              {l('participants_limit_single', {
                value: maxParticipantsToUse,
                entity: l(bookingType),
                declension: ['participants_limit_single_genitive', 'participants_limit_plural'],
              })}
            </Label>
          </Flex>
          <PositionedBox
            bottom='0'
            position='sticky'
          >
            <Button
              onClick={handleNextClick}
              disabled={isLoading}
            >
              {isMultiSession && (sharedConfig || currentSessionIdx >= totalSessionCount - 1)
                ? l('preview_booking') || 'Preview Booking'
                : l('next')}
            </Button>
          </PositionedBox>
        </Box>
      </ScrollableWrapper>
    </GridLayout>
  )
}
